import {
  Button,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from '@mui/material';
import { TemplateType } from 'compobot-dynamic-page';
import { Control, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CreateGuiVersionType } from 'types/compobotGuiVersion.type';
import CustomPageParametersSection from './sections/CustomPageParametersSection';
import DisplayDateSection from './sections/DisplayDateSection';
import DropdownSection from './sections/DropdownSection';
import GuiPropertySection from './sections/GuiPropertySection';
import HelloPageSection from './sections/HelloPageSection';
import OverlayColorSection from './sections/OverlayColorSection';
import PretextSection from './sections/PretextSection';
import QrCodeSection from './sections/QrCodeSection';
import SubtitleSection from './sections/SubtitleSection';
import TitleSection from './sections/TitleSection';

interface FormContentProps {
  control: Control<CreateGuiVersionType>;
  errors: FieldErrors<CreateGuiVersionType>;
  onSubmit: () => void;
  isEditMode?: boolean;
  useCustomPageParams?: boolean;
  setUseCustomPageParams?: (value: boolean) => void;

  // Word counters
  pretextEnCount: number;
  pretextHuCount: number;
  setPretextEnCount: (count: number) => void;
  setPretextHuCount: (count: number) => void;
  titleEnCount: number;
  titleHuCount: number;
  setTitleEnCount: (count: number) => void;
  setTitleHuCount: (count: number) => void;
  subtitleEnCount: number;
  subtitleHuCount: number;
  setSubtitleEnCount: (count: number) => void;
  setSubtitleHuCount: (count: number) => void;

  // Color props
  colors: {
    pretext: string;
    title: string;
    subtitle: string;
    overlay: string;
  };
  onPretextColorChange: (color: string) => void;
  onTitleColorChange: (color: string) => void;
  onSubtitleColorChange: (color: string) => void;
  onOverlayColorChange: (color: string) => void;
}

export default function FormContent({
  control,
  errors,
  onSubmit,
  isEditMode = false,
  useCustomPageParams = true,
  setUseCustomPageParams,
  pretextEnCount,
  pretextHuCount,
  setPretextEnCount,
  setPretextHuCount,
  titleEnCount,
  titleHuCount,
  setTitleEnCount,
  setTitleHuCount,
  subtitleEnCount,
  subtitleHuCount,
  setSubtitleEnCount,
  setSubtitleHuCount,
  colors,
  onPretextColorChange,
  onTitleColorChange,
  onSubtitleColorChange,
  onOverlayColorChange,
}: FormContentProps) {
  const { t } = useTranslation();

  const templateOptions = [
    { value: TemplateType.THEME_01, label: 'Theme 01' },
    { value: TemplateType.THEME_02, label: 'Theme 02' },
    { value: TemplateType.THEME_03, label: 'Theme 03' },
  ];

  const languageOptions = [
    { value: 'en', label: t('createGuiVersionPage.english') },
    { value: 'hu', label: t('createGuiVersionPage.hungarian') },
  ];

  return (
    <form onSubmit={onSubmit}>
      <Grid
        container
        spacing={2}
        sx={{
          mb: 2,
          px: 2,
          py: 1,
        }}
      >
        <GuiPropertySection control={control} errors={errors} />
        <DropdownSection
          control={control}
          error={errors.language}
          options={languageOptions}
          name={t('createGuiVersionPage.language')}
          fieldName="language"
          defaultValue="en"
        />
        <HelloPageSection control={control} errors={errors} />
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            {t('createGuiVersionPage.customPageParameters')}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={useCustomPageParams}
                onChange={(e) => setUseCustomPageParams?.(e.target.checked)}
                color="primary"
              />
            }
            label={t('createGuiVersionPage.enableCustomPageParams')}
          />
        </Grid>
        {useCustomPageParams && (
          <Grid
            container
            spacing={2}
            sx={{
              mb: 2,
              px: 2,
              py: 1,
              overflow: 'auto',
              height: '40vh',
            }}
          >
            <OverlayColorSection
              overlayColor={colors.overlay}
              onOverlayColorChange={onOverlayColorChange}
            />
            <CustomPageParametersSection control={control} errors={errors} />
            <QrCodeSection control={control} errors={errors} />
            <PretextSection
              control={control}
              errors={errors}
              pretextEnCount={pretextEnCount}
              pretextHuCount={pretextHuCount}
              setPretextEnCount={setPretextEnCount}
              setPretextHuCount={setPretextHuCount}
              pretextColor={colors.pretext}
              onPretextColorChange={onPretextColorChange}
            />
            <TitleSection
              control={control}
              errors={errors}
              titleEnCount={titleEnCount}
              titleHuCount={titleHuCount}
              setTitleEnCount={setTitleEnCount}
              setTitleHuCount={setTitleHuCount}
              titleColor={colors.title}
              onTitleColorChange={onTitleColorChange}
            />
            <SubtitleSection
              control={control}
              errors={errors}
              subtitleEnCount={subtitleEnCount}
              subtitleHuCount={subtitleHuCount}
              setSubtitleEnCount={setSubtitleEnCount}
              setSubtitleHuCount={setSubtitleHuCount}
              subtitleColor={colors.subtitle}
              onSubtitleColorChange={onSubtitleColorChange}
            />
            <DisplayDateSection control={control} errors={errors} />
            <DropdownSection
              control={control}
              error={errors.templateType}
              options={templateOptions}
              name={t('createGuiVersionPage.templateType')}
              fieldName="templateType"
              defaultValue={TemplateType.THEME_01}
            />
          </Grid>
        )}
      </Grid>
      <Grid item xs={12}>
        <Button type="submit" variant="contained" fullWidth>
          {isEditMode
            ? t('createGuiVersionPage.updateButtonLabel')
            : t('createGuiVersionPage.submitButtonLabel')}
        </Button>
      </Grid>
    </form>
  );
}
